'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {usePopperTooltip} from 'react-popper-tooltip';
import isMobile from 'is-mobile';
import {colorDarkest, colorWhite} from '@nib-components/theme';
import {Box} from '@nib/layout';

type ArrowProps = ReturnType<ReturnType<typeof usePopperTooltip>['getArrowProps']>;
type TooltipProps = ReturnType<ReturnType<typeof usePopperTooltip>['getTooltipProps']>;

const validPlacements = ['top', 'bottom'] as const;
type Placement = (typeof validPlacements)[number];

const validModes = ['light', 'dark'] as const;
type Modes = (typeof validModes)[number];

interface TriggerProps {
  ref: ReturnType<typeof usePopperTooltip>['setTooltipRef'];
  tabIndex: 0;
  'aria-describedby': string;
}

export interface BaseTooltipProps {
  id: string;
  tooltipContents: React.ReactNode;
  placement?: Placement;
  mode?: Modes;
}

export interface TooltipRendererProps extends BaseTooltipProps {
  children: (renderProps: {triggerProps: TriggerProps}) => React.ReactNode;
}

interface TooltipArrowProps extends ArrowProps {
  readonly mode: Modes;
}

// eslint-disable-next-line no-unused-vars
export const TooltipArrow = styled(({mode, arrowProps, tooltipContents, tooltipProps, ...rest}) => <div {...rest} />)<TooltipArrowProps>`
  height: 1rem;
  width: 1rem;
  pointer-events: none;
  color: ${({mode}) => (mode === 'dark' ? colorDarkest : colorWhite)};

  ::before {
    content: '';
    display: block;
    width: 0;
    height: 0;
    margin: auto;
    position: absolute;
    border: 0.5rem solid transparent;
  }
`;
TooltipArrow.displayName = 'TooltipArrow';

export const TooltipContainer = styled.div<TooltipContainerProps>`
  box-sizing: border-box;
  z-index: 9999;

  &[data-popper-placement^='top'] ${TooltipArrow} {
    top: 100%;

    ::before {
      border-top-color: currentColor;
    }
  }

  &[data-popper-placement^='bottom'] ${TooltipArrow} {
    bottom: 100%;

    ::before {
      border-bottom-color: currentColor;
    }
  }
`;
TooltipContainer.displayName = 'TooltipContainer';

interface TooltipElementProps extends BaseTooltipProps {
  tooltipProps: TooltipProps;
  arrowProps: ArrowProps;
}

interface TooltipContainerProps {
  id: string;
  mode?: Modes;
  hidden?: boolean;
}

export const TooltipElement = (props: TooltipElementProps) => {
  const {id, tooltipContents, mode, tooltipProps, arrowProps} = props;
  const isDark = mode === 'dark';
  return (
    <TooltipContainer id={id} role="tooltip" {...tooltipProps}>
      <Box padding={4} background={isDark ? 'darkest' : 'white'} color={isDark ? 'white' : 'darkest'} borderRadius="standard" maxWidth="280px">
        {tooltipContents}
        <TooltipArrow mode={mode} {...arrowProps} />
      </Box>
    </TooltipContainer>
  );
};

const TooltipRenderer = (props: TooltipRendererProps) => {
  const {id, tooltipContents, placement = 'top', mode = 'dark', children} = props;
  const {getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef, visible} = usePopperTooltip(
    {
      placement,
      trigger: [isMobile() ? 'click' : 'hover', 'focus']
    },
    {
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            padding: 16
          }
        },
        {
          name: 'offset',
          options: {
            offset: [0, 16]
          }
        }
      ]
    }
  );

  return (
    <>
      {children({
        triggerProps: {
          tabIndex: 0,
          ref: setTriggerRef,
          'aria-describedby': id
        }
      })}

      {visible && (
        <TooltipElement
          id={id}
          mode={mode}
          placement={placement}
          tooltipProps={getTooltipProps({className: 'tooltip-container', ref: setTooltipRef})}
          arrowProps={getArrowProps()}
          tooltipContents={tooltipContents}
        />
      )}
    </>
  );
};

TooltipRenderer.propTypes = {
  id: PropTypes.string.isRequired,
  tooltipContents: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(validPlacements),
  mode: PropTypes.oneOf(validModes),
  children: PropTypes.func.isRequired
};

export default TooltipRenderer;
